import { useCallback, useEffect } from 'react';
import { AgentflowDocService } from '../../service/AgentflowDocService';
import { useNotificationStore } from '../../store/storeNotifications';
import { useAgentflowDocStore } from '../../store/storeAgentflowDoc';

interface UseAgentflowDocReturn {
  md: string | null;
  isLoading: boolean;
  isSaving: boolean;
  isRegenerating: boolean;
  error: string | null;
  hasDoc: boolean;
  loadDoc: () => Promise<void>;
  saveDoc: (md: string) => Promise<void>;
  regenerateDoc: () => Promise<void>;
}

export const useAgentflowDoc = (
  flowId: string | undefined,
): UseAgentflowDocReturn => {
  const setMd = useAgentflowDocStore(state => state.setMd);
  const setLoading = useAgentflowDocStore(state => state.setLoading);
  const setSaving = useAgentflowDocStore(state => state.setSaving);
  const setRegenerating = useAgentflowDocStore(state => state.setRegenerating);
  const setError = useAgentflowDocStore(state => state.setError);
  const clearTransient = useAgentflowDocStore(state => state.clearTransient);
  const md = useAgentflowDocStore(state =>
    flowId ? state.mdByFlowId[flowId] ?? null : null,
  );
  const isLoading = useAgentflowDocStore(state => state.isLoading);
  const isSaving = useAgentflowDocStore(state => state.isSaving);
  const isRegenerating = useAgentflowDocStore(state => state.isRegenerating);
  const error = useAgentflowDocStore(state => state.error);
  const setNotification = useNotificationStore(state => state.setNotification);

  const loadDoc = useCallback(async () => {
    if (!flowId) return;
    if (flowId in useAgentflowDocStore.getState().mdByFlowId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await AgentflowDocService.getDoc(flowId);
      setMd(flowId, response?.data?.md ?? null);
    } catch (err: any) {
      const message = err?.message || 'Failed to load agent documentation';
      setError(message);
      setNotification({ type: 'error', message, duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [flowId, setMd, setError, setLoading, setNotification]);

  const saveDoc = useCallback(
    async (next: string) => {
      if (!flowId) return;
      setSaving(true);
      setError(null);
      try {
        const response = await AgentflowDocService.updateDoc(flowId, next);
        setMd(flowId, response?.data?.md ?? next);
        setNotification({
          type: 'success',
          message: 'Agent documentation saved',
          duration: 2500,
        });
      } catch (err: any) {
        const message = err?.message || 'Failed to save agent documentation';
        setError(message);
        setNotification({ type: 'error', message, duration: 4000 });
      } finally {
        setSaving(false);
      }
    },
    [flowId, setMd, setError, setNotification, setSaving],
  );

  const regenerateDoc = useCallback(async () => {
    if (!flowId) return;
    setRegenerating(true);
    setError(null);
    try {
      const response = await AgentflowDocService.regenerateDoc(flowId);
      setMd(flowId, response?.data?.md ?? null);
      setNotification({
        type: 'success',
        message: 'Agent documentation regenerated',
        duration: 2500,
      });
    } catch (err: any) {
      const message =
        err?.message || 'Failed to regenerate agent documentation';
      setError(message);
      setNotification({ type: 'error', message, duration: 4000 });
    } finally {
      setRegenerating(false);
    }
  }, [flowId, setMd, setError, setNotification, setRegenerating]);

  useEffect(() => {
    return () => {
      clearTransient();
    };
  }, [clearTransient]);

  return {
    md,
    isLoading,
    isSaving,
    isRegenerating,
    error,
    hasDoc: md !== null && md.trim().length > 0,
    loadDoc,
    saveDoc,
    regenerateDoc,
  };
};
